/**
 * Minimal JSON HTTP client for ApiDataProvider.
 * Authentication: every request asks the auth layer for an access token (see src/auth/authAdapter.ts)
 * and sends it as `Authorization: Bearer <token>`. Cookie sessions work too: requests use
 * `credentials: 'include'`. Replace or extend this file to fit the backend (CSRF header, retries…).
 */
import { DataError } from '@/services/DataProvider';

export type TokenGetter = () => Promise<string | null>;

export interface HttpClientOptions {
  baseUrl: string;
  getToken?: TokenGetter;
  fetchImpl?: typeof fetch;
}

export class HttpClient {
  constructor(private readonly opts: HttpClientOptions) {}

  async request<T>(method: string, path: string, body?: unknown, query?: Record<string, string | undefined>): Promise<T> {
    if (!this.opts.baseUrl) throw new DataError('The API address is not configured. Set VITE_API_BASE_URL.', 0);
    const url = new URL(this.opts.baseUrl + path);
    Object.entries(query || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') url.searchParams.set(k, v);
    });
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const token = this.opts.getToken ? await this.opts.getToken() : null;
    if (token) headers.Authorization = `Bearer ${token}`;
    let res: Response;
    try {
      res = await (this.opts.fetchImpl || fetch)(url.toString(), { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: 'include' });
    } catch {
      throw new DataError('The server could not be reached. Check the connection and try again.', 0);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const data = text ? safeJson(text) : undefined;
    if (!res.ok) {
      const m = data && typeof data === 'object' ? (data as { message?: unknown }).message : undefined;
      const msg = typeof m === 'string' && m ? m : `Request failed (${res.status}).`;
      throw new DataError(msg, res.status, data);
    }
    return data as T;
  }
  get<T>(path: string, query?: Record<string, string | undefined>) {
    return this.request<T>('GET', path, undefined, query);
  }
  post<T>(path: string, body?: unknown) {
    return this.request<T>('POST', path, body);
  }
  patch<T>(path: string, body: unknown) {
    return this.request<T>('PATCH', path, body);
  }
  put<T>(path: string, body: unknown) {
    return this.request<T>('PUT', path, body);
  }
  delete(path: string) {
    return this.request<void>('DELETE', path);
  }
}

function safeJson(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}
